import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { db, storage } from "@lib/firebase";
import {
  collection,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { jsPDF } from "jspdf";
import { InputFieldRace } from "../common/InputFieldRace";
import { VoucherInput } from "../common/VoucherInput";
import { serverTimestamp } from "firebase/firestore";

export const FormPendaftaran = ({ labelColor = "text-slate-800" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { title, price, foreignerPrice, eventId } = location.state || {};
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    namaLengkap: "",
    email: "",
    nomorIdentitas: "",
    tanggalLahir: "",
    gender: "",
    negara: "Indonesia",
    kontakDarurat: "",
    golonganDarah: "",
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSnapReady, setIsSnapReady] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [voucherCode, setVoucherCode] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(price || 0);
  const [collectionName, setCollectionName] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const [distance, setDistance] = useState(null);

  // Tentukan bahasa berdasarkan negara peserta
  const getLanguageByCountry = () => {
    return formData.negara === "Indonesia" ? "id" : "en";
  };

  // Update finalPrice berdasarkan negara
  useEffect(() => {
    if (price && foreignerPrice) {
      const basePrice = formData.negara === "Indonesia" ? price : foreignerPrice;
      const newFinalPrice = Math.floor(basePrice * (1 - discount / 100));
      setFinalPrice(newFinalPrice);
    }
  }, [formData.negara, price, foreignerPrice, discount]);

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Identity Number";
    const withSpaces = str.replace(/([A-Z])/g, " $1"). trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1). toLowerCase())
      .join(" ");
  };

  const fetchCollectionName = async (eventId) => {
    try {
      const eventRef = doc(db, "semuaKegiatan", eventId);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        setCollectionName(eventData.collectionName || "pendaftaranSEATRC");
        return eventData;
      }
      throw new Error("Dokumen event tidak ditemukan.");
    } catch (error) {
      setErrorMsg("Gagal mengambil data event.  Silakan coba lagi.");
      showNotification(
        "error",
        "Failed to fetch event data. Please try again."
      );
      return null;
    }
  };

  const fetchCategoryDetails = async (eventId, title) => {
    try {
      const eventRef = doc(db, "semuaKegiatan", eventId);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc. exists()) {
        const eventData = eventDoc.data();
        if (eventData.type === "seminar") {
          setCategoryName(eventData.title);
          setDistance(0);
        } else {
          const categories = eventData.categories || [];
          let selectedCategory = categories.find((cat) =>
            title.includes(cat.categoryName)
          );
          if (!selectedCategory && categories.length > 0) {
            selectedCategory = categories. find(
              (cat) => cat.categoryName === "Vertical 7K"
            );
          }
          if (selectedCategory) {
            setCategoryName(selectedCategory.categoryName);
            setDistance(selectedCategory.distance);
          } else {
            throw new Error("Category not found in event data.");
          }
        }
      } else {
        throw new Error("Event document not found.");
      }
    } catch (error) {
      setErrorMsg("Failed to load category details. Please try again.");
      showNotification(
        "error",
        "Failed to load category details. Please try again."
      );
    }
  };

  const checkRegistration = async (collectionName) => {
    if (!user || !eventId || !collectionName) {
      return;
    }

    try {
      const q = query(
        collection(db, collectionName),
        where("uid", "==", user.uid),
        where("eventId", "==", eventId),
        where("kategori", "==", categoryName),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (! querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        const status = data.transaction_status || "unknown";
        const paymentType = data.payment_type || "unknown";
        const harga = data.harga || 0;

        if (
          (status === "settlement" &&
            (paymentType === "free" || harga === 0)) ||
          (status === "pending" && paymentType !== "free") ||
          (status === "settlement" && paymentType !== "free" && data.snapToken)
        ) {
          setRegistrationStatus("registered");
          setTransactionStatus(status);
          setSnapToken(data.snapToken || null);
          setOrderId(data.orderId || null);
          setVoucherCode(data.voucherCode || null);
        } else if (
          ["expire", "cancel", "deny"].includes(status) &&
          ! data.snapToken
        ) {
          await deleteDoc(doc. ref);
          setRegistrationStatus("not_registered");
          setTransactionStatus(null);
          setSnapToken(null);
          setOrderId(null);
          setVoucherCode(null);
        }
      } else {
        setRegistrationStatus("not_registered");
      }
    } catch (err) {
      setErrorMsg("Failed to check registration status. Please try again.");
      showNotification(
        "error",
        "Failed to check registration status. Please try again."
      );
    }
  };

  useEffect(() => {
    if (! eventId || !title || !price || !foreignerPrice) {
      showNotification(
        "error",
        "Please select a category from the event page."
      );
      navigate("/");
      return;
    }

    if (! Number.isInteger(price) || ! Number.isInteger(foreignerPrice)) {
      setErrorMsg("Harga event tidak valid.");
      showNotification("error", "Invalid event price.");
      navigate("/");
      return;
    }

    const initialize = async () => {
      if (! user) {
        navigate("/login");
        showNotification("error", "Please login to register.");
        return;
      }

      try {
        const eventData = await fetchCollectionName(eventId);
        if (! eventData) {
          navigate("/");
          return;
        }
        await fetchCategoryDetails(eventId, title);

        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        let userData = {};

        if (docSnap. exists()) {
          userData = docSnap.data();
          let namaDepan = userData.namaDepan || "";
          let namaBelakang = userData.namaBelakang || "";
          let namaLengkap = userData.namaLengkap || "";

          if (! namaDepan && !namaBelakang && namaLengkap) {
            const namaSplit = namaLengkap. trim().split(/\s+/);
            namaDepan = namaSplit[0] || "";
            namaBelakang = namaSplit. slice(1).join(" ") || "";
          }

          const rootFields = {
            namaDepan,
            namaBelakang,
            namaLengkap:
              namaLengkap || `${namaDepan} ${namaBelakang}`. trim(),
            email: userData.email || user.email || "",
            nomorIdentitas: userData.nomorIdentitas || "",
            tanggalLahir: userData. tanggalLahir || "",
            gender: userData.gender || "",
            negara: userData.negara || "Indonesia",
            kontakDarurat: userData.kontakDarurat || "",
            golonganDarah:
              eventId === "JOTR" ? userData.golonganDarah || "" : "",
          };

          setFormData((prev) => ({
            ...prev,
            ...rootFields,
          }));
        } else {
          const namaSplit = (user.displayName || "").trim(). split(/\s+/);
          const namaDepan = namaSplit[0] || "";
          const namaBelakang = namaSplit.slice(1). join(" ") || "";
          setFormData((prev) => ({
            ...prev,
            namaDepan,
            namaBelakang,
            namaLengkap:
              namaDepan && namaBelakang
                ? `${namaDepan} ${namaBelakang}`.trim()
                : "",
            email: user.email || "",
          }));
        }

        if (collectionName && categoryName) {
          await checkRegistration(collectionName);
        }
      } catch (err) {
        setErrorMsg("Gagal mengambil data pengguna.");
        showNotification("error", "Failed to fetch user data.");
        return;
      }
    };

    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);

    if (eventId && title && price && user) {
      initialize();
    } else {
      showNotification(
        "error",
        "Please select a category from the event page."
      );
      navigate("/");
    }

    return () => clearInterval(checkSnap);
  }, [
    eventId,
    title,
    price,
    foreignerPrice,
    navigate,
    showNotification,
    user,
    collectionName,
    categoryName,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "namaDepan" || name === "namaBelakang") {
        updated.namaLengkap = `${updated.namaDepan || ""} ${
          updated.namaBelakang || ""
        }`.trim();
      }
      if (name === "namaLengkap") {
        const namaSplit = value.trim().split(/\s+/);
        updated.namaDepan = namaSplit[0] || "";
        updated.namaBelakang = namaSplit.slice(1).join(" ") || "";
      }
      return updated;
    });
  };

  const handleApplyVoucher = (code, discountPercentage) => {
    setVoucherCode(code);
    setDiscount(discountPercentage);
    const basePrice = formData.negara === "Indonesia" ? price : foreignerPrice;
    const newFinalPrice = Math.floor(
      basePrice * (1 - discountPercentage / 100)
    );
    setFinalPrice(newFinalPrice);
  };

  const updateUserData = async (updatedFields) => {
    try {
      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);
      const existingData = docSnap.exists() ? docSnap.data() : {};

      const fieldsToUpdate = {};
      Object.keys(updatedFields).forEach((key) => {
        if (
          updatedFields[key] !== undefined &&
          updatedFields[key] !== ""
        ) {
          fieldsToUpdate[key] = updatedFields[key];
        } else if (! existingData[key]) {
          fieldsToUpdate[key] = "";
        }
      });
      fieldsToUpdate.updatedAt = serverTimestamp();

      await updateDoc(docRef, fieldsToUpdate);
    } catch (err) {
      setErrorMsg("Gagal memperbarui data pengguna.");
      showNotification("error", "Failed to update user data.");
    }
  };

  const buildDeclarationLetterContent = (language) => {
    const nameLabel = language === "en" ? "Name" : "Nama";
    const dateLabel = language === "en" ? "Date" : "Tanggal";

    if (language === "en") {
      const paragraphs = [
        `I hereby declare that I voluntarily and consciously register myself as a participant in the Jakarta Open Trail Run (JOTR) Cianjur Series 2026 - Southeast Asia Trail Running Championship (SEATRC), which will be held at Taman Nasional Gunung Gede Pangrango, Cianjur, Jawa Barat.   `,

        `I acknowledge that this event is physically and mentally challenging and involves inherent risks, including but not limited to extreme weather conditions, difficult terrain, significant altitude changes, and other potential health hazards.  I confirm that I have adequately prepared myself and am in good health to safely participate in this event.  `,

        `I acknowledge that the event organizers, volunteers, sponsors, and all related parties cannot be held legally liable for injuries, accidents, losses, or other emergencies that may occur during my participation.   I accept full responsibility for my personal safety and health throughout the event and hereby release all parties from any and all legal claims arising from or related to my participation.   `,

        `I commit to the following:`,
        [
          "Adhering to all rules and instructions provided by the organizers and authorized officials.",
          "Maintaining sportsmanship, safety, and order throughout the duration of the event.",
          "Refraining from any actions that may harm myself, other participants, organizers, or the surrounding environment.",
          "Respecting the natural environment and preserving the integrity of Mount Gede Pangrango National Park.",
        ],

        `I grant full and irrevocable permission to the organizers to use any and all documentation, including photographs, videos, or other media that include my image or likeness, for purposes of publication, promotion, social media, and event documentation, without requiring my further consent.  `,

        `I confirm that all personal information provided during the registration process is accurate and truthful, and I declare that I have no medical conditions that would prevent my safe participation in this event. `,

        `By signing this declaration, I confirm that I have carefully read, fully understood, and voluntarily agree to all terms and conditions stated herein without any coercion or pressure from any party. This declaration represents my personal commitment and acceptance of responsibility as a participant in the Jakarta Open Trail Run (JOTR) Cianjur Series 2026.   `,
      ];
      return { title: "DECLARATION LETTER", paragraphs, nameLabel, dateLabel };
    } else {
      const paragraphs = [
        `Dengan ini saya menyatakan bahwa saya secara sukarela dan sadar mendaftarkan diri sebagai peserta dalam kegiatan Jakarta Open Trail Run (JOTR) Cianjur Series 2026 - Southeast Asia Trail Running Championship (SEATRC) yang akan dilaksanakan di Taman Nasional Gunung Gede Pangrango, Cianjur, Jawa Barat.  `,

        `Saya memahami bahwa kegiatan ini menantang secara fisik dan mental, serta memiliki potensi risiko, seperti kondisi cuaca ekstrem, kontur medan yang berat, ketinggian yang signifikan, dan gangguan kesehatan lainnya. Oleh karena itu, saya menyatakan bahwa saya telah mempersiapkan diri secara optimal untuk mengikuti kegiatan ini dan dalam kondisi kesehatan yang baik. `,

        `Saya menyadari bahwa panitia penyelenggara, relawan, sponsor, maupun pihak-pihak terkait lainnya tidak bertanggung jawab secara hukum atas risiko cedera, kecelakaan, kehilangan, atau kejadian darurat lainnya yang mungkin saya alami selama mengikuti rangkaian kegiatan.    Seluruh tanggung jawab atas keselamatan dan kesehatan pribadi selama kegiatan merupakan tanggung jawab saya sendiri, dan saya melepaskan semua pihak dari segala bentuk tuntutan hukum atas kejadian yang mungkin timbul selama atau akibat keikutsertaan saya dalam kegiatan ini. `,

        `Saya berkomitmen untuk:`,
        [
          "Mengikuti seluruh peraturan dan arahan dari panitia dan pihak berwenang.",
          "Menjaga sportivitas, keamanan, dan ketertiban selama kegiatan berlangsung.",
          "Tidak melakukan tindakan yang merugikan diri sendiri, peserta lain, panitia, maupun lingkungan sekitar.",
          "Menghormati lingkungan alami dan menjaga kelestarian Taman Nasional Gunung Gede Pangrango.",
        ],

        `Saya juga memberikan izin penuh kepada panitia untuk menggunakan dokumentasi (foto, video, atau lainnya) yang memuat identitas atau citra saya untuk keperluan publikasi, promosi, media sosial, dan dokumentasi kegiatan tanpa memerlukan izin tambahan.   `,

        `Saya menegaskan bahwa data pribadi saya yang diberikan dalam pendaftaran adalah akurat dan benar, serta saya tidak memiliki kondisi kesehatan yang menghalangi partisipasi dalam kegiatan ini. `,

        `Dengan ini, saya menyatakan telah membaca, memahami, dan menyetujui seluruh isi pernyataan ini dengan sadar, sukarela, dan tanpa tekanan dari pihak mana pun.    Pernyataan ini saya buat sebagai bentuk komitmen dan tanggung jawab saya sebagai peserta dalam Jakarta Open Trail Run (JOTR) Cianjur Series 2026.`,
      ];
      return { title: "SURAT PERNYATAAN", paragraphs, nameLabel, dateLabel };
    }
  };

  const simpanSuratPernyataanSebagaiPDF = async () => {
    const language = getLanguageByCountry();
    const content = buildDeclarationLetterContent(language);

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const marginLeft = 20;
    const marginTop = 20;
    const lineHeight = 7;
    const bulletIndent = 5;
    let yPosition = marginTop;

    // Header
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text(content.title, 105, yPosition, { align: "center" });
    yPosition += lineHeight * 2;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");

    // Informasi peserta
    const namaLengkap = formData.namaLengkap || "Unknown";
    const today = new Date();
    const tanggal = today.toLocaleDateString(
      language === "en" ? "en-US" : "id-ID",
      {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }
    );

    pdf.text(`${content.nameLabel}: ${namaLengkap}`, marginLeft, yPosition);
    yPosition += lineHeight;
    pdf.text(`${content.dateLabel}: ${tanggal}`, marginLeft, yPosition);
    yPosition += lineHeight * 2;

    // Isi surat
    content.paragraphs.forEach((item) => {
      if (Array.isArray(item)) {
        item.forEach((bullet) => {
          const lines = pdf.splitTextToSize(bullet, 170 - bulletIndent);
          lines.forEach((line, index) => {
            if (yPosition + lineHeight > 277) {
              pdf.addPage();
              yPosition = marginTop;
            }
            const text = index === 0 ? `• ${line}` : line;
            const xOffset =
              index === 0 ?  marginLeft : marginLeft + bulletIndent;
            pdf.text(text, xOffset, yPosition);
            yPosition += lineHeight;
          });
        });
      } else {
        const lines = pdf.splitTextToSize(item, 170);
        lines.forEach((line) => {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(line, marginLeft, yPosition);
          yPosition += lineHeight;
        });
      }
      yPosition += lineHeight;
    });

    if (yPosition + lineHeight * 3 > 277) {
      pdf.addPage();
      yPosition = marginTop;
    }
    const signoff = language === "en" ? "Sincerely," : "Hormat saya,";
    pdf.text(signoff, 170, yPosition, { align: "right" });
    yPosition += lineHeight * 2;
    pdf.setFont("helvetica", "bold");
    pdf.text(namaLengkap, 170, yPosition, { align: "right" });

    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.text(`Page ${i} of ${pageCount}`, 105, 287, { align: "center" });
    }

    const pdfBlob = pdf.output("blob");
    const fileName = `Surat_Pernyataan_${namaLengkap.replace(
      /\s+/g,
      "_"
    )}_${eventId}_${language}.pdf`;

    try {
      console.log(`🔄 Mengunggah surat pernyataan untuk user ${user.uid}...`);
      const suratQuery = query(
        collection(db, "suratPernyataan"),
        where("userId", "==", user.uid),
        where("eventId", "==", eventId),
        limit(1)
      );
      const suratSnapshot = await getDocs(suratQuery);
      if (! suratSnapshot.empty) {
        console.log(
          `⚠️ Surat pernyataan untuk user ${user.uid} sudah ada, melewati... `
        );
        return;
      }

      const storageRef = ref(
        storage,
        `suratPernyataan/${user.uid}/${fileName}`
      );
      await uploadBytes(storageRef, pdfBlob);
      const downloadURL = await getDownloadURL(storageRef);
      console.log(
        `✅ File ${fileName} berhasil diunggah untuk user ${user.uid}`
      );

      const docData = {
        namaLengkap,
        tanggal,
        fileName,
        downloadURL,
        language,
        createdAt: serverTimestamp(),
        userId: user.uid,
        eventId,
      };

      await addDoc(collection(db, "suratPernyataan"), docData);
      console.log(
        `✅ Metadata surat pernyataan disimpan untuk user ${user.uid}`
      );
    } catch (err) {
      console.error(
        `❌ Gagal menyimpan surat pernyataan untuk user ${user.uid}:`,
        err. message
      );
      setErrorMsg("Failed to save declaration letter.");
      showNotification("error", "Failed to save declaration letter.");
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!isSnapReady) {
      setErrorMsg("Midtrans sedang disiapkan, mohon tunggu sebentar.");
      showNotification(
        "error",
        "Midtrans is being prepared, please wait a moment."
      );
      setLoading(false);
      return;
    }

    if (!isChecked) {
      setErrorMsg("Harap setujui Surat Pernyataan sebelum melanjutkan.");
      showNotification(
        "error",
        "Please agree to the Declaration Letter before proceeding."
      );
      setLoading(false);
      return;
    }

    if (! categoryName || (eventId !== "PelatihanPelatih" && ! distance)) {
      setErrorMsg("Data kategori atau jarak tidak lengkap.");
      showNotification("error", "Category or distance data is incomplete.");
      setLoading(false);
      return;
    }

    const validateFormat = (field, value, negara = "Indonesia") => {
      const label = humanize(field);

      if (! value || value.trim() === "") {
        return `${label} is required`;
      }
      if (
        (field === "namaDepan" || field === "namaBelakang") &&
        value.length < 1
      ) {
        return `${label} is required. `;
      }
      if (field === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return `${label} must be in a valid format (e.g., user@example.com).`;
      }
      if (
        field === "kontakDarurat" &&
        value.length < 8
      ) {
        return `${label} must be at least 8 digits.`;
      }
      if (field === "nomorIdentitas") {
        if (value.length < 5) {
          return `${label} must be at least 5 characters.`;
        }
      }
      if (field === "tanggalLahir") {
        const date = new Date(value);
        const eventDate = new Date("2026-09-27");
        const minAgeDate = new Date(
          eventDate.getFullYear() - 15,
          eventDate.getMonth(),
          eventDate.getDate()
        );
        if (isNaN(date.getTime()) || date >= eventDate) {
          return `${label} must be in the past before event date, e.g., "2011-09-27"`;
        } else if (date > minAgeDate) {
          return `Participant must be at least 15 years old, e.g., "2011-09-27" or earlier`;
        }
      }
      if (field === "golonganDarah" && ! ["A", "B", "AB", "O"].includes(value)) {
        return `${label} must be A, B, AB, or O.`;
      }
      if (field === "gender" && !["Male", "Female"]. includes(value)) {
        return `${label} must be Male or Female.`;
      }
      return "";
    };

    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "email",
      "nomorIdentitas",
      "tanggalLahir",
      "gender",
      "negara",
      "kontakDarurat",
      ...(eventId === "JOTR"
        ? ["golonganDarah"]
        : []),
    ];

    const errors = {};
    for (const field of requiredFields) {
      const error = validateFormat(field, formData[field], formData.negara);
      if (error) {
        errors[field] = error;
      }
    }

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      setErrorMsg(firstError);
      showNotification("error", firstError);
      setFormData((prev) => ({ ...prev, errors }));
      setLoading(false);
      return;
    }

    if (! Number.isInteger(finalPrice)) {
      setErrorMsg("Harga akhir harus berupa bilangan bulat.");
      showNotification("error", "Final price must be an integer.");
      setLoading(false);
      return;
    }

    try {
      if (! eventId || ! title || !price || !foreignerPrice) {
        throw new Error(
          "Event data incomplete. Please try again from the category page."
        );
      }

      const sanitizeValue = (value) => {
        if (typeof value === "string") {
          const trimmed = value.trim();
          return trimmed === "" ? undefined : trimmed.replace(/\s+/g, " ");
        }
        return value;
      };

      const sanitizedFormData = {
        namaDepan: sanitizeValue(formData.namaDepan),
        namaBelakang: sanitizeValue(formData.namaBelakang),
        namaLengkap: sanitizeValue(formData.namaLengkap),
        email: sanitizeValue(formData.email),
        nomorIdentitas: formData.nomorIdentitas
          ? formData.nomorIdentitas.trim().replace(/\s+/g, "")
          : undefined,
        tanggalLahir: sanitizeValue(formData.tanggalLahir),
        gender: sanitizeValue(formData.gender),
        negara: sanitizeValue(formData.negara),
        kontakDarurat: sanitizeValue(formData.kontakDarurat),
        golonganDarah: eventId === "JOTR" ? sanitizeValue(formData.golonganDarah) : undefined,
      };

      const sanitizedErrors = {};
      for (const field of requiredFields) {
        const error = validateFormat(
          field,
          sanitizedFormData[field],
          sanitizedFormData.negara
        );
        if (error) {
          sanitizedErrors[field] = error;
        }
      }

      if (Object.keys(sanitizedErrors).length > 0) {
        const firstError = Object.values(sanitizedErrors)[0];
        setErrorMsg(firstError);
        showNotification("error", firstError);
        setFormData((prev) => ({ ...prev, errors: sanitizedErrors }));
        setLoading(false);
        return;
      }

      const updatedUserData = {
        namaDepan: sanitizedFormData.namaDepan,
        namaBelakang: sanitizedFormData.namaBelakang,
        namaLengkap: sanitizedFormData.namaLengkap,
        nomorIdentitas: sanitizedFormData.nomorIdentitas,
        tanggalLahir: sanitizedFormData.tanggalLahir,
        gender: sanitizedFormData.gender,
        negara: sanitizedFormData.negara,
        kontakDarurat: sanitizedFormData.kontakDarurat,
        golonganDarah: eventId === "JOTR" ? sanitizedFormData.golonganDarah : undefined,
      };

      await updateUserData(updatedUserData);

      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      if (! userDoc.exists()) {
        throw new Error("User data not found in Firestore after update.");
      }
      const userData = userDoc.data();

      const requiredUserFields = [
        "namaDepan",
        "namaBelakang",
        "nomorIdentitas",
        "tanggalLahir",
        "golonganDarah",
        "gender",
        "kontakDarurat",
        "negara",
      ];
      const missingUserFields = requiredUserFields.filter(
        (field) => !userData[field] || userData[field].trim() === ""
      );
      if (missingUserFields.length > 0) {
        throw new Error(
          `User data incomplete: ${missingUserFields.join(", ")} is required.`
        );
      }

      await simpanSuratPernyataanSebagaiPDF();

      const payload = {
        namaDepan: sanitizedFormData.namaDepan,
        namaBelakang: sanitizedFormData.namaBelakang,
        namaLengkap: sanitizedFormData.namaLengkap,
        email: sanitizedFormData.email,
        nomorIdentitas: sanitizedFormData.nomorIdentitas,
        tanggalLahir: sanitizedFormData.tanggalLahir,
        gender: sanitizedFormData.gender,
        negara: sanitizedFormData.negara,
        kontakDarurat: sanitizedFormData.kontakDarurat,
        golonganDarah: eventId === "JOTR" ? sanitizedFormData.golonganDarah : undefined,
        kategori: eventId === "JOTR" ? categoryName : undefined,
        seminar: eventId === "PelatihanPelatih" ? categoryName : undefined,
        distance: distance || 0,
        harga: Math.floor(finalPrice),
        eventId,
        voucherCode,
        uid: user.uid,
        collectionName,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/seatrc/register`,
        payload
      );

      if (finalPrice === 0) {
        setSuccessMsg("Registration Successful!");
        showNotification("success", "Registration Successful!");
        setOrderId(response.data.orderId);
        setTimeout(
          () =>
            navigate(
              `/myregistration? order_id=${response.data.orderId}&transaction_status=settlement`
            ),
          2000
        );
        return;
      }

      const { snapToken, orderId } = response.data;
      setSnapToken(snapToken);
      setOrderId(orderId);

      window.snap.pay(snapToken, {
        onSuccess: async () => {
          setSuccessMsg("Pembayaran berhasil!");
          showNotification("success", "Payment successful!");
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=settlement`
          );
        },
        onPending: async () => {
          setSuccessMsg("Pembayaran sedang diproses.");
          showNotification("warning", "Payment is being processed.");
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=pending`
          );
        },
        onError: () => {
          setErrorMsg("Pembayaran gagal.  Silakan coba lagi.");
          showNotification("error", "Payment failed. Please try again.");
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=failed`
          );
        },
        onClose: () => {},
      });
    } catch (error) {
      let errorMessage = "Registration failed. Please try again.";
      let errorDetails = "";
      if (error.response?.data) {
        errorMessage = error.response.data.message || errorMessage;
        if (error.response.data.errors) {
          const errors = error.response.data.errors;
          errorDetails = JSON.stringify(errors, null, 2);
        }
      } else {
        errorMessage = error.message || errorMessage;
      }
      setErrorMsg(`${errorMessage}${errorDetails ?  `: ${errorDetails}` : ""}`);
      showNotification(
        "error",
        `${errorMessage}${errorDetails ? `: ${errorDetails}` : ""}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBayar = async () => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (! isSnapReady || !window.snap) {
      setErrorMsg("Midtrans belum siap.  Silakan coba lagi.");
      showNotification("error", "Midtrans is not ready. Please try again.");
      setLoading(false);
      return;
    }

    if (! snapToken || !orderId) {
      setErrorMsg("Payment token or order ID not found.");
      showNotification(
        "error",
        "Payment token or order ID not found."
      );
      setLoading(false);
      return;
    }

    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          setSuccessMsg("Payment successful!");
          showNotification("success", "Payment successful!");
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=settlement`
          );
        },
        onPending: async () => {
          setSuccessMsg("Payment is being processed.");
          showNotification("warning", "Payment is being processed.");
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=pending`
          );
        },
        onError: () => {
          setErrorMsg("Payment failed. Please try again.");
          showNotification("error", "Payment failed. Please try again.");
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=failed`
          );
        },
        onClose: () => {},
      });
    } catch (err) {
      setErrorMsg("Failed to process payment. Please try again.");
      showNotification("error", "Failed to process payment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isSettled = transactionStatus === "settlement";
  const isPending = transactionStatus === "pending";
  const isFormDisabled = isSettled || isPending;

  return (
    <div className="min-h-screen bg-white shadow-xl px-4 sm:px-6 lg:px-8 z-0">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4 py-20 overflow-visible">
        <div className="w-full md:w-3/4 max-w-4xl bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md">
          <h2 className="text-2xl font-semibold text-left mb-4">
            SEATRC Registration Form
          </h2>
          <form id="form-pendaftaran" onSubmit={handleSubmit}>
            <InputFieldRace
              formData={formData}
              handleChange={handleChange}
              isFormDisabled={isFormDisabled}
              labelColor={labelColor}
              eventId={eventId}
              categoryName={categoryName}
              updateUserData={updateUserData}
            />
          </form>
        </div>

        <div className="w-full md:w-1/4 max-w-sm bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-left">
            {categoryName || title}
          </h2>
          {discount === 100 ? (
            <div className="bg-lime-100 border-lime-500 text-lime-700 p-4 rounded-xl">
              <p className="font-bold text-md mb-2">Registration Access Granted</p>
              <p className="text-sm">You may proceed with your registration.</p>
            </div>
          ) : (
             <div className="text-slate-700 ">
              <p className="font-bold text-md mb-2">Registration Reserved</p>
              <p className="text-sm">
                Registration is currently reserved for SEATRC Athletes with a valid
                Voucher Code.
              </p>
            </div>
          )}

          {(errorMsg || successMsg) && (
            <p
              className={`text-left font-medium ${
                errorMsg ?  "text-rose-600" : "text-slate-800"
              }`}
            >
              {errorMsg || successMsg}
            </p>
          )}

          {registrationStatus === "registered" && isSettled && (
            <p className="text-left text-slate-800 font-medium">
              You are already registered for this event. 
            </p>
          )}

          {registrationStatus === "registered" && isPending && (
            <p className="text-left text-slate-800 font-medium">
              Waiting for payment.
            </p>
          )}

          <hr className="border-t border-gray-300" />

          <VoucherInput
            onApplyVoucher={handleApplyVoucher}
            disabled={isFormDisabled}
            eventId={eventId}
            category={categoryName}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="syarat"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="w-5 h-5 focus:ring-2 focus:ring-red-600"
              disabled={isFormDisabled}
            />
            <label htmlFor="syarat" className="text-sm">
              I have read and agree to the{" "}
              <a
                href="/SuratPernyataan"
                target="_blank"
                className="text-red-600 underline"
              >
                Declaration Letter
              </a>
            </label>
          </div>

          <div>
            {isSettled ?  (
              <button
                type="button"
                className="w-full py-3 bg-slate-400 font-semibold rounded-xl cursor-not-allowed opacity-50"
                disabled
              >
                Already Registered
              </button>
            ) : isPending ? (
              <button
                type="button"
                onClick={handleBayar}
                className={`w-full py-3 bg-red-600 hover:bg-red-700 transition-all duration-300 text-white font-semibold rounded-xl ${
                  loading ? "cursor-not-allowed opacity-50" : ""
                }`}
                disabled={loading}
              >
                {loading ? "Loading..." : "Complete Payment"}
              </button>
            ) : (
              <button
                type="submit"
                form="form-pendaftaran"
                className={`w-full py-3 bg-red-900 hover:bg-red-700 transition-all duration-300 text-white font-semibold rounded-xl ${
                  loading || ! isChecked || ! voucherCode
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
                disabled={loading || !isChecked || !voucherCode}
              >
                {loading
                  ? "Loading..."
                  : "Register"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormPendaftaran;